import { localStorageSupported } from '$lib/helper/browser';
import type { Notification } from '$lib/model/notification';
import { apiStore } from '$lib/stores/api';
import { type ListStore } from '$lib/stores/infinity-list';
import { unreadReviewsCountStore } from '$lib/stores/review';
import type { NotificationStatsGetApiResponse } from '$lib/types/api/notification';
import type { Axios } from 'axios';
import { DateTime } from 'luxon';
import { get, writable } from 'svelte/store';

export const notificationPanelStore = writable<boolean>(false);

const notificationStatsStoreDefinition = () => {
	const { subscribe, set, update } = writable<{
		unread: number;
		knownNotificationsIds: number[];
	}>({ unread: 0, knownNotificationsIds: [] });

	return {
		subscribe,
		set,
		update,
		refresh: () => {
			(get(apiStore) as Axios)
				.get<NotificationStatsGetApiResponse>('/notification/stats')
				.then((response) => {
					notificationStatsStore.set({
						unread: response.data.unread,
						knownNotificationsIds: get(notificationStatsStore).knownNotificationsIds
					});
					unreadReviewsCountStore.set(response.data.unreadReviews);
				});
		}
	};
};

export const notificationStatsStore = notificationStatsStoreDefinition();

export const mutedDeviceStore = writable<boolean | null>(null);

mutedDeviceStore.subscribe((value) => {
	if (value === null || !localStorageSupported()) {
		return;
	}

	if (value) {
		localStorage.setItem('muted', 'true');
	} else {
		localStorage.setItem('muted', 'false');
	}
});

const notificationStoreDefinition = () => {
	const { subscribe, set, update } = writable<ListStore<Notification>>({
		data: [],
		isFresh: true,
		apiSearchUrl: '/notification/search'
	});

	return {
		subscribe,
		set,
		update,
		markAsRead: (notification: Notification) => {
			update((store) => {
				notification.isRead = true;
				notificationStatsStore.update((value) => {
					return {
						unread: value.unread - 1,
						knownNotificationsIds: value.knownNotificationsIds
					};
				});

				(get(apiStore) as Axios)
					.post(`/notification/read`, { id: notification.id })
					.catch(() => {
						notification.isRead = false;
						notificationStatsStore.update((value) => {
							return {
								unread: value.unread + 1,
								knownNotificationsIds: value.knownNotificationsIds
							};
						});
					});

				return store;
			});
		},
		markAsUnread: (notification: Notification) => {
			update((store) => {
				notification.isRead = false;
				notificationStatsStore.update((value) => {
					return {
						unread: value.unread + 1,
						knownNotificationsIds: value.knownNotificationsIds
					};
				});

				(get(apiStore) as Axios)
					.post(`/notification/unread`, { id: notification.id })
					.catch(() => {
						notification.isRead = true;
						notificationStatsStore.update((value) => {
							return {
								unread: value.unread - 1,
								knownNotificationsIds: value.knownNotificationsIds
							};
						});
					});
				return store;
			});
		},
		markAllAsRead: () => {
			update((store) => {
				store.data.forEach((notification) => {
					notification.isRead = true;
				});
				const currentUnread = get(notificationStatsStore).unread;

				notificationStatsStore.update((value) => {
					return { unread: 0, knownNotificationsIds: value.knownNotificationsIds };
				});

				(get(apiStore) as Axios).post(`/notification/read`).catch(() => {
					store.data.forEach((notification) => {
						notification.isRead = false;
					});
					notificationStatsStore.update((value) => {
						return {
							unread: currentUnread,
							knownNotificationsIds: value.knownNotificationsIds
						};
					});
				});

				return store;
			});
		},
		remove: (notification: Notification) => {
			update((store) => {
				const index = store.data.findIndex((n) => n.id === notification.id);
				if (index !== -1) {
					store.data.splice(index, 1);
				}
				return store;
			});
		},
		removeAll: () => {
			update((store) => {
				store.data = [];
				return store;
			});
		}
	};
};

export const notificationStore = notificationStoreDefinition();

export function transformApiNotificationToNotification(notification: Notification): Notification {
	notification.createdAt = DateTime.fromISO(notification.createdAt as unknown as string);
	return notification;
}
